import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AdminShell } from '@/shells/AdminShell';
import { PortalShell } from '@/shells/PortalShell';
import { Login } from '@/routes/login';
import { TwoFactor } from '@/routes/two-factor';
import { Dashboard } from '@/routes/dashboard';
import { PortalDashboard } from '@/routes/portal-dashboard';
import { ClientsIndex } from '@/routes/clients';
import { NewClient } from '@/routes/clients/new';
import { ClientDetail } from '@/routes/clients/client-detail';
import { EditClient } from '@/routes/clients/edit';
import { ContactsIndex } from '@/routes/contacts';
import { ContactDetail } from '@/routes/contacts/detail';
import { LeadsIndex } from '@/routes/leads';
import { LeadsPipeline } from '@/routes/leads/pipeline';
import { LeadDetail } from '@/routes/leads/lead-detail';
import { LeadsTasks } from '@/routes/leads/tasks';
import { NewLead } from '@/routes/leads/new';
import { ProjectsIndex } from '@/routes/projects';
import { ProjectDetail } from '@/routes/projects/project-detail';
import { PortalProjects } from '@/routes/portal/projects';
import { BlogIndex } from '@/routes/blog';
import { BlogEditor } from '@/routes/blog/editor';
import { SitesIndex } from '@/routes/sites';
import { SiteDetail } from '@/routes/sites/site-detail';
import { ClientFormDetail } from '@/routes/sites/form-detail';
import { FormSubmissionsIndex } from '@/routes/forms';
import { PortalAssets } from '@/routes/assets-portal';
import { PortalProposals } from '@/routes/portal/approvables';
import { PortalApprovableDetail } from '@/routes/portal/approvable-detail';
import { PortalInvoices, PortalInvoiceDetail } from '@/routes/portal/invoices';
import { PortalDocuments } from '@/routes/portal/documents';
import { PortalVault } from '@/routes/portal/vault';
import { PortalSubscriptions } from '@/routes/portal/subscriptions';
import { PortalTeam } from '@/routes/portal/team';
import { PortalActivity } from '@/routes/portal/activity';
import { PortalRequests } from '@/routes/portal/requests';
import { RequestsIndex } from '@/routes/requests-index';
import { Messages } from '@/routes/messages';
import { CallsIndex } from '@/routes/calls';
import { PermissionGate } from '@/routes/permission-gate';
import { RequireAuth } from '@/routes/guard';
import { NotFound } from '@/routes/not-found';
import { Settings } from '@/routes/settings';
import { NotificationsPage } from '@/routes/notifications';
import { Accounting } from '@/routes/accounting';
import { DocumentsIndex } from '@/routes/documents-index';
import { ApprovablesIndex } from '@/routes/approvables-index';
import { InvoicesIndex } from '@/routes/invoices-index';
import { SubscriptionsIndex } from '@/routes/subscriptions-index';
import { AssetsIndex } from '@/routes/assets-index';
import { TimeIndex } from '@/routes/time-index';
import { InvitationAccept } from '@/routes/invitation-accept';
import { ToastProvider } from '@/components/toast';
import { ConfirmProvider } from '@/components/confirm';
import '@/index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: false, retry: 1 },
  },
});

function detectSurface(): 'admin' | 'portal' {
  const host = window.location.hostname;
  return host.startsWith('admin.') ? 'admin' : 'portal';
}

const surface = detectSurface();
const Shell = surface === 'admin' ? AdminShell : PortalShell;

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <ConfirmProvider>
        <BrowserRouter>
          <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/two-factor-challenge" element={<TwoFactor />} />
          <Route path="/invitations/:token" element={<InvitationAccept />} />
          <Route
            element={
              <RequireAuth>
                <Shell />
              </RequireAuth>
            }
          >
            <Route index element={surface === 'admin' ? <Dashboard /> : <PortalDashboard />} />
            <Route
              path="/projects"
              element={
                <PermissionGate permission="projects">
                  {surface === 'admin' ? <ProjectsIndex /> : <PortalProjects />}
                </PermissionGate>
              }
            />
            <Route
              path="/projects/:id"
              element={
                <PermissionGate permission="projects">
                  <ProjectDetail />
                </PermissionGate>
              }
            />
            <Route
              path="/blog"
              element={
                <PermissionGate permission="blog">
                  <BlogIndex />
                </PermissionGate>
              }
            />
            <Route
              path="/blog/new"
              element={
                <PermissionGate permission="blog">
                  <BlogEditor />
                </PermissionGate>
              }
            />
            <Route
              path="/blog/:postId"
              element={
                <PermissionGate permission="blog">
                  <BlogEditor />
                </PermissionGate>
              }
            />
            {surface === 'portal' ? (
              <>
                <Route
                  path="/assets"
                  element={
                    <PermissionGate permission="assets">
                      <PortalAssets />
                    </PermissionGate>
                  }
                />
                <Route
                  path="/proposals"
                  element={
                    <PermissionGate permission="proposals.view">
                      <PortalProposals />
                    </PermissionGate>
                  }
                />
                <Route
                  path="/proposals/:id"
                  element={
                    <PermissionGate permission="proposals.view">
                      <PortalApprovableDetail />
                    </PermissionGate>
                  }
                />
                <Route
                  path="/invoices"
                  element={
                    <PermissionGate permission="invoices.view">
                      <PortalInvoices />
                    </PermissionGate>
                  }
                />
                <Route
                  path="/invoices/:id"
                  element={
                    <PermissionGate permission="invoices.view">
                      <PortalInvoiceDetail />
                    </PermissionGate>
                  }
                />
                <Route
                  path="/documents"
                  element={
                    <PermissionGate permission="documents">
                      <PortalDocuments />
                    </PermissionGate>
                  }
                />
                <Route
                  path="/vault"
                  element={
                    <PermissionGate permission="credentials">
                      <PortalVault />
                    </PermissionGate>
                  }
                />
                <Route
                  path="/subscriptions"
                  element={
                    <PermissionGate permission="subscriptions">
                      <PortalSubscriptions />
                    </PermissionGate>
                  }
                />
                <Route
                  path="/team"
                  element={
                    <PermissionGate permission="contacts">
                      <PortalTeam />
                    </PermissionGate>
                  }
                />
                <Route
                  path="/activity"
                  element={
                    <PermissionGate permission="activity">
                      <PortalActivity />
                    </PermissionGate>
                  }
                />
                <Route
                  path="/forms"
                  element={
                    <PermissionGate permission="form_submissions">
                      <FormSubmissionsIndex />
                    </PermissionGate>
                  }
                />
                <Route
                  path="/requests"
                  element={
                    <PermissionGate permission="requests">
                      <PortalRequests />
                    </PermissionGate>
                  }
                />
              </>
            ) : null}
            {surface === 'admin' ? (
              <>
                <Route path="/clients" element={<ClientsIndex />} />
                <Route path="/clients/new" element={<NewClient />} />
                <Route path="/requests" element={<RequestsIndex />} />
                <Route path="/messages" element={<Messages />} />
                <Route path="/calls" element={<CallsIndex />} />
                <Route path="/clients/:id" element={<ClientDetail />} />
                <Route path="/clients/:id/edit" element={<EditClient />} />
                <Route path="/sites" element={<SitesIndex />} />
                <Route path="/sites/:id" element={<SiteDetail />} />
                <Route path="/sites/:id/forms/:formId" element={<ClientFormDetail />} />
                <Route path="/forms" element={<FormSubmissionsIndex />} />
                <Route path="/contacts" element={<ContactsIndex />} />
                <Route path="/contacts/:id" element={<ContactDetail />} />
                <Route path="/leads" element={<LeadsIndex />} />
                <Route path="/leads/pipeline" element={<LeadsPipeline />} />
                <Route path="/leads/tasks" element={<LeadsTasks />} />
                <Route path="/leads/new" element={<NewLead />} />
                <Route path="/leads/:id" element={<LeadDetail />} />
                <Route path="/time" element={<TimeIndex />} />
                <Route path="/assets" element={<AssetsIndex />} />
                <Route path="/documents" element={<DocumentsIndex />} />
                <Route path="/approvables" element={<ApprovablesIndex />} />
                <Route path="/invoices" element={<InvoicesIndex />} />
                <Route path="/subscriptions" element={<SubscriptionsIndex />} />
                {/* Receipts now live as a tab on Subscriptions; old links still land there. */}
                <Route path="/receipts" element={<Navigate to="/subscriptions?tab=receipts" replace />} />
                <Route path="/accounting" element={<Accounting />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/inbox" element={<NotificationsPage />} />
                <Route path="/notifications" element={<NotificationsPage />} />
              </>
            ) : null}
            <Route path="*" element={<NotFound />} />
          </Route>
          </Routes>
        </BrowserRouter>
        </ConfirmProvider>
      </ToastProvider>
    </QueryClientProvider>
  </StrictMode>,
);
